import { useState, useCallback } from 'react';

/**
 * `<img>` con shimmer mientras carga en vez de aparecer de golpe cuando
 * termina. `boxClassName`/`boxStyle` van en el contenedor (aspect-ratio,
 * fondo); `imgClassName` en la propia foto (object-fit, hover, etc.) — así
 * el llamador conserva exactamente el mismo layout que ya tenía.
 *
 * `priority`: la foto se pinta SIN el fundido, visible desde el primer
 * frame. Es para la imagen que decide el LCP de la página.
 *
 * Por qué hace falta esa salida. El fundido se apoya en `.progressive-img
 * { opacity: 0 }` hasta que un `onLoad` de React marca `data-loaded`. En
 * una página PRE-RENDERIZADA eso sale caro: el HTML ya trae la foto, pero
 * el navegador no puede pintarla hasta que el bundle descargue, ejecute e
 * hidrate. Medido en /catalogo: el 53% del LCP era "render delay" —tiempo
 * con la imagen ya descargada y aún invisible— y el LCP se iba a 7.6 s en
 * una página que pinta su primer contenido en 1.1 s. Para la foto principal
 * el fundido no vale ese precio; para las de más abajo sí, y ahí se queda.
 */
// Anchos que server.js sabe generar (IMG_WIDTHS). Solo aplican a fotos que
// pasan por nuestro proxy /img/; cualquier otra URL se sirve tal cual.
const SRCSET_WIDTHS = [320, 480, 640, 960];

function srcSetFor(src) {
    if (typeof src !== 'string' || !src.startsWith('/img/') || src.includes('?')) return undefined;
    return SRCSET_WIDTHS.map((w) => `${src}?w=${w} ${w}w`).join(', ');
}

export default function ProgressiveImage({
    src,
    alt,
    boxClassName = '',
    boxStyle,
    imgClassName = '',
    loading = 'lazy',
    priority = false,
    // Ancho con el que se pinta la foto, para que el navegador elija la
    // versión reducida adecuada. Sin `sizes`, no se genera srcset.
    sizes,
    ...rest
}) {
    const srcSet = sizes ? srcSetFor(src) : undefined;
    const [loaded, setLoaded] = useState(false);

    // Una imagen que ya está completa cuando React la adopta NO dispara
    // `onLoad`: el evento ya pasó. Ocurría de verdad —con la foto en caché,
    // o al hidratar HTML pre-renderizado— y la dejaba en opacity 0 para
    // siempre, invisible. Este ref lo comprueba en el momento de montar,
    // que es la única forma de enterarse de un evento que ya ocurrió.
    const measure = useCallback((node) => {
        if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
    }, []);

    const shown = priority || loaded;

    return (
        <div className={`relative overflow-hidden ${boxClassName}`} style={boxStyle}>
            {!priority && (
                <div aria-hidden="true" className="progressive-shimmer absolute inset-0" data-loaded={loaded} />
            )}
            <img
                ref={measure}
                src={src}
                srcSet={srcSet}
                sizes={srcSet ? sizes : undefined}
                alt={alt}
                loading={loading}
                decoding="async"
                onLoad={() => setLoaded(true)}
                className={`progressive-img ${imgClassName}`}
                data-loaded={shown}
                {...rest}
            />
        </div>
    );
}
